import slugify from 'slugify';

import { stringify } from 'query-string';

import { Option } from '@gojob/ui-bits/PlacesAutocomplete/useRemoteOptions';

export type BasePathType = {
  type: string;
  pageIndex?: number;
};

export type PathType = BasePathType &
  (
    | { type: 'job-offer'; shortId?: string; caption?: string; city?: string }
    | { type: 'keyword'; keyword?: string }
    | { type: 'sector'; name?: string }
    | { type: 'list' }
    | {
        type: 'search';
        keyword?: string;
        city?: Option | null;
        radius?: number;
      }
    | {
        type: 'city';
        name?: string;
        department?: {
          name?: string;
          index?: string;
        };
      }
    | {
        type: 'keyword-city';
        keyword?: string;
        cityName?: string;
        department?: {
          name?: string;
          index?: string;
        };
      }
    | {
        type: 'department';
        name?: string;
        index?: string;
      }
  );

export const PREFIX = `offres-emploi-interim`;
export const PREFIXED_PATH_TYPES: (PathType['type'] | undefined)[] = [
  'keyword',
  'city',
  'keyword-city',
  'department',
  'search',
  'sector',
];

export const createPathname = (action?: PathType): string => {
  switch (action?.type) {
    case 'search': {
      const { keyword, city = {}, radius } = action;
      const ville = city?.nom;
      const [lng, lat] = city?.centre?.coordinates || [];

      return `?${stringify({ ville, lat, lng, radius, q: keyword?.toLocaleLowerCase() })}`;
    }

    case 'sector': {
      const { name = '' } = action;

      return slugify(name, { lower: true });
    }

    case 'list': {
      return `${PREFIX}/toutes-nos-missions-interim`;
    }

    case 'job-offer': {
      const { shortId: jobId = '', caption, city } = action;
      if (caption && city) {
        return `offre/${slugify(`${caption}-${city}-${jobId}`)}`;
      }
      return `offre/${slugify(jobId)}`;
    }

    case 'keyword': {
      const { keyword = '' } = action;
      return slugify(keyword, { lower: true });
    }

    case 'department': {
      const { index, name = '' } = action;
      if (index) {
        return slugify(`${index}-${name}`, { lower: true });
      }
      return slugify(name, { lower: true });
    }

    case 'city': {
      const { name = '', department = {} } = action;
      const prefix = createPathname({ type: 'department', ...department });
      if (prefix) {
        return slugify(`${prefix}-${name}`, { lower: true });
      }
      return slugify(name, { lower: true });
    }

    case 'keyword-city': {
      const { keyword = '', cityName = '', department = {} } = action;
      const keywordPath = createPathname({ keyword, type: 'keyword' });
      const cityPath = createPathname({ department, type: 'city', name: cityName });

      return [keywordPath, cityPath].filter((path) => path !== '').join('/');
    }

    default:
      break;
  }

  return '';
};

/** Adds the page index to the created pathname */
export const withPagination = (pathCreator: typeof createPath) => (action?: PathType) => {
  const path = createPath(action);

  if (path && typeof action?.pageIndex === 'number') {
    if (action.pageIndex === 0) {
      return `${path}/`;
    }
    return `${path}/${action?.pageIndex + 1}`;
  }
  return path;
};

const createPath = (action?: PathType) => {
  const pathname = createPathname(action);

  if (PREFIXED_PATH_TYPES.indexOf(action?.type) > -1) {
    return `/${PREFIX}/${pathname}`;
  }
  return `/${pathname}`;
};

export default withPagination(createPath);
